import type { IProfileSummaryMeta } from '@setplex/tria-api'
import type { OptimisticProfile } from './index.h'

import { HttpErrors, is } from '@bldsoft/cappie/rest'
import { api } from '@setplex/tria-api'
import { attach, combine, createStore, sample } from 'effector'
import { features } from '~/core/flags'
import { model as sessionModel } from '~/entities/session'
import { noop } from '~/shared/helpers'
import {
  add,
  decreaseRemainingProfiles,
  formatProfile,
  increaseRemainingProfiles,
  remove,
  set,
} from './lib'

export const getProfilesSummaryFx = attach({
  source: features.$featuredFlags,
  effect: async (featuredFlags) =>
    featuredFlags.kidsModulesEnabled
      ? await api.profiles.getProfilesSummaryFx()
      : await api.profiles.getProfilesFx(),
})

export const getActiveProfileFx = attach({
  effect: api.profiles.getActiveProfileFx,
})

export const createProfileFx = attach({
  effect: api.profiles.createProfileFx,
})

export const updateProfileFx = attach({
  effect: api.profiles.updateProfileFx,
})

export const removeProfileFx = attach({
  effect: api.profiles.removeProfileFx,
})

export const updateProfileActiveStateFx = attach({
  effect: api.profiles.updateProfileActiveStateFx,
})

/**
 * optimistic profiles list
 */
const $optimisticRawProfiles = createStore<OptimisticProfile[]>([])
  // load all profiles
  .on(getProfilesSummaryFx.doneData, (_, summary) => summary.profiles)

  // load active profile
  .on(getActiveProfileFx.doneData, (list, profile) => add(list, profile))

  // create profile
  .on(createProfileFx, (list, profile) =>
    add(list, { ...profile, id: Infinity })
  )
  .on(createProfileFx.done, (list, { result, params }) =>
    set(list, Infinity, (p) => ({ ...p, ...result, ...params }))
  )
  .on(createProfileFx.fail, (list) => remove(list, Infinity))

  // update profile
  .on(updateProfileFx, (list, profile) =>
    set(list, profile.id, (p) => ({ ...profile, _original: p }))
  )
  .on(updateProfileFx.done, (list, { params: profile }) =>
    set(list, profile.id, ({ _original, ...p }) => p)
  )
  .on(updateProfileFx.fail, (list, { params: profile }) =>
    set(list, profile.id, ({ _original }) => _original!)
  )

  // remove profile
  .on(removeProfileFx, (list, { id }) =>
    set(list, id, (p) => ({ ...p, _deleted: true }))
  )
  .on(removeProfileFx.done, (list, { params: { id } }) => remove(list, id))
  .on(removeProfileFx.fail, (list, { error, params }) => {
    const { id } = params

    if (is(error, HttpErrors.notFound)) return remove(list, id)

    set(list, id, ({ _deleted, ...p }) => p)
  })
  // reset list
  .reset(api.session.signOutFx.doneData, sessionModel.effectiveUnauthorized)

export const $profilesSummaryMeta = createStore<IProfileSummaryMeta | null>(
  null
)
  .on(getProfilesSummaryFx.doneData, (_, summary) => summary.meta)
  // create profile
  .on(createProfileFx, (summary, profile) =>
    decreaseRemainingProfiles({
      summary,
      kidsProfile: profile.kidsProfile,
    })
  )
  .on(createProfileFx.fail, (summary, { params: profile }) =>
    increaseRemainingProfiles({
      summary,
      kidsProfile: profile.kidsProfile,
    })
  )
  .on(removeProfileFx, (summary, { kidsProfile }) =>
    increaseRemainingProfiles({
      summary,
      kidsProfile,
    })
  )
  .on(removeProfileFx.fail, (summary, { params: { kidsProfile } }) =>
    decreaseRemainingProfiles({
      summary,
      kidsProfile,
    })
  )

  .reset(api.session.signOutFx.doneData, sessionModel.effectiveUnauthorized)

/**
 * optimistic profiles list
 */
export const $optimisticProfiles = combine(
  { profiles: $optimisticRawProfiles, meta: $profilesSummaryMeta },
  ({ profiles, meta }) =>
    profiles
      .filter((p) =>
        meta?.kidsModuleAvailable ? !p._deleted : !p.kidsProfile && !p._deleted
      )
      .map(({ _original, _deleted, ...p }) => formatProfile(p))
)

/**
 * optimistic active profile raw id
 */
const $activeProfileRawId = createStore<[number, number?]>([0])
  .on(api.session.$session, (_, session) => [session?.profileId ?? 0])
  .on(getActiveProfileFx.doneData, (_, { id }) => [id ?? 0])
  .on(updateProfileActiveStateFx, ([old], id) => [id, old])
  .on(updateProfileActiveStateFx.done, ([id]) => [id])
  .on(updateProfileActiveStateFx.fail, ([_, old]) => [old ?? 0])
  .reset(api.session.signOutFx.doneData, sessionModel.effectiveUnauthorized)

// update profiles when some error on profile select
sample({
  clock: updateProfileActiveStateFx.fail,
  fn: noop,
  target: getProfilesSummaryFx,
})

// update profiles locked states
sample({
  clock: [
    api.subscriber.updateProfileSwitchLockedFx.doneData,
    updateProfileActiveStateFx.doneData,
  ],
  target: getProfilesSummaryFx,
})

/**
 * optimistic active profile id
 */
export const $activeProfileId = $activeProfileRawId.map(([id]) => id)

/**
 * optimistic active profile
 */
export const $activeProfile = combine(
  $optimisticProfiles,
  $activeProfileId,
  (profiles, id) =>
    profiles.length === 0 || id === 0
      ? null
      : (profiles.find((p) => p.id === id) ?? null)
)
